import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { Container, Row, Col, Form, Spinner, Alert } from "react-bootstrap";
import { motion, AnimatePresence } from "framer-motion";
import {
  FaFire,
  FaTrashAlt,
  FaChartLine,
  FaExchangeAlt,
  FaCoins,
  FaShieldAlt,
  FaUserTie,
  FaUmbrellaBeach,
  FaExclamationTriangle,
  FaCheckCircle,
  FaCalendarAlt,
  FaMoneyBillWave,
  FaFileInvoiceDollar,
  FaBalanceScale
} from "react-icons/fa";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
} from "chart.js";
import { Bar } from "react-chartjs-2";
import "../css/WaFire.css";

// Chart.js 컴포넌트 등록
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

// 선택 가능한 미국 고배당/인컴 ETF 메타데이터
const ETF_DATABASE = {
  JEPQ: { name: "JPMorgan Nasdaq Equity Premium Income", tag: "나스닥 커버드콜", yieldEst: "9~11%", desc: "나스닥100 기반 고배당 월배당의 대명사" },
  SPYI: { name: "NEOS S&P 500 High Income", tag: "S&P500 옵션 인컴", yieldEst: "11~12%", desc: "S&P500 기반 세금 효율적 월배당" },
  JEPI: { name: "JPMorgan Equity Premium Income", tag: "S&P500 저변동 배당", yieldEst: "7~8%", desc: "낮은 변동성과 안정적 방어형 인컴" },
  GPIQ: { name: "Goldman Sachs Nasdaq-100 Core Premium", tag: "골드만 나스닥 커버드콜", yieldEst: "9~11%", desc: "골드만삭스의 나스닥 월배당 ETF" },
  GPIX: { name: "Goldman Sachs S&P 500 Core Premium", tag: "골드만 S&P500 커버드콜", yieldEst: "8~9%", desc: "골드만삭스의 S&P500 월배당 ETF" },
  QQQI: { name: "NEOS Nasdaq-100 High Income", tag: "나스닥 옵션 인컴", yieldEst: "13~14%", desc: "NEOS의 나스닥 초고배당 월배당" },
  SCHD: { name: "Schwab U.S. Dividend Equity", tag: "미국 정통 배당성장", yieldEst: "3.5~4%", desc: "국민 배당성장주 (분기배당이지만 배당성장의 왕)" },
  DIVO: { name: "Amplify CWP Enhanced Dividend", tag: "우량 배당주 + 전술 옵션", yieldEst: "4.5~5.5%", desc: "배당성장과 선별적 콜옵션의 조화" },
  TLTW: { name: "iShares 20+ Year Treasury Bond BuyWrite", tag: "미국 20년 국채 커버드콜", yieldEst: "15~18%", desc: "초장기 미국채 기반 초고배당 인컴" },
  SGOV: { name: "iShares 0-3 Month Treasury Bond", tag: "미국 초단기 국채 (파킹)", yieldEst: "4.5~5.2%", desc: "무위험 달러 파킹통장 성격의 안전자산" }
};

const AVAILABLE_SYMBOLS = Object.keys(ETF_DATABASE);

// 금액 한글 포맷 유틸
const formatKoreanAmount = (num) => {
  if (!num || isNaN(num)) return "0원";
  const abs = Math.abs(num);
  const uk = Math.floor(abs / 100000000);
  const man = Math.floor((abs % 100000000) / 10000);
  const won = Math.floor(abs % 10000);

  let result = [];
  if (uk > 0) result.push(`${uk}억`);
  if (man > 0) result.push(`${man.toLocaleString()}만`);
  if (won > 0 && uk === 0 && man === 0) result.push(`${won.toLocaleString()}`);

  const str = result.join(" ") + " 원";
  return num < 0 ? `-${str}` : str;
};

// 원화 단위 콤마 포맷
const formatKRW = (num) => {
  if (num === null || num === undefined || isNaN(num)) return "0";
  return Math.round(num).toLocaleString("ko-KR");
};

// 달러 단위 포맷
const formatUSD = (num) => {
  if (num === null || num === undefined || isNaN(num)) return "0.00";
  return Number(num).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

// 위트 있는 개구리 판정 코멘트
const getFrogVerdict = (monthlyNet) => {
  if (monthlyNet < 1500000) {
    return { title: "🐸 삼각김밥 & 라면 생존 파이어", desc: "기본 생계는 가능하지만 허리띠를 바짝 졸라매야 해요!" };
  } else if (monthlyNet < 2500000) {
    return { title: "🐸 1인 가구 자립 파이어", desc: "숨만 쉬어도 월세와 생활비 해결! 잔업 거부권 획득!" };
  } else if (monthlyNet < 4000000) {
    return { title: "🐸 회사 탈출 정식 승인 (중산층 파이어)", desc: "직장 상사 얼굴 안 봐도 되는 진정한 경제적 자유!" };
  } else {
    return { title: "👑 갓물주 부럽지 않은 황제 파이어!", desc: "매달 월급 이상이 통장에 꽂히는 여유로운 배당 라이프!" };
  }
};

const WaFireSimulator = () => {
  // 1. 입력 상태 관리
  const [totalCapital, setTotalCapital] = useState(300000000); // 기본 3억 원
  const [startDate, setStartDate] = useState("2023-01-01");
  const [status, setStatus] = useState("UNEMPLOYED"); // "UNEMPLOYED" | "EMPLOYED"
  const [otherAnnualIncome, setOtherAnnualIncome] = useState(0);

  // ETF 포트폴리오 비중 (기본: JEPQ 50%, SPYI 50%)
  const [allocations, setAllocations] = useState([
    { symbol: "JEPQ", ratio: 0.5 },
    { symbol: "SPYI", ratio: 0.5 }
  ]);

  // 2. API 실행 상태 및 응답 데이터
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [result, setResult] = useState(null);

  // 비중 총합 계산
  const totalRatio = useMemo(() => {
    return allocations.reduce((acc, cur) => acc + (Number(cur.ratio) || 0), 0);
  }, [allocations]);

  const totalPercent = useMemo(() => {
    return Math.round(totalRatio * 100);
  }, [totalRatio]);

  const isRatioValid = Math.abs(totalRatio - 1.0) < 0.001;

  // 빠른 자본금 증액 핸들러
  const handleAddCapital = (amount) => {
    setTotalCapital((prev) => Math.max(0, prev + amount));
  };

  // 비중 변경 핸들러 (% 단위로 입력받아 0.0~1.0 ratio로 변환)
  const handleRatioChange = (index, percent) => {
    const newRatio = Math.max(0, Math.min(100, Number(percent) || 0)) / 100;
    setAllocations((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], ratio: newRatio };
      return next;
    });
  };

  // 종목 추가
  const handleAddSymbol = (symbol) => {
    if (allocations.some((a) => a.symbol === symbol)) return;
    setAllocations((prev) => [...prev, { symbol, ratio: 0 }]);
  };

  // 종목 삭제
  const handleRemoveSymbol = (index) => {
    if (allocations.length <= 1) {
      alert("최소 1개 이상의 ETF 종목이 필요합니다.");
      return;
    }
    setAllocations((prev) => prev.filter((_, i) => i !== index));
  };

  // 1/N 균등 배분
  const handleEqualWeight = () => {
    const count = allocations.length;
    if (count === 0) return;
    const each = Number((1 / count).toFixed(4));
    setAllocations((prev) =>
      prev.map((item, idx) => ({
        ...item,
        // 마지막 항목에 나머지 오차 보정
        ratio: idx === count - 1 ? Number((1 - each * (count - 1)).toFixed(4)) : each
      }))
    );
  };

  // 100% 자동 맞춤
  const handleAutoAdjust = () => {
    if (allocations.length === 0) return;
    const sumExceptLast = allocations
      .slice(0, -1)
      .reduce((acc, cur) => acc + cur.ratio, 0);
    const remaining = Math.max(0, Number((1 - sumExceptLast).toFixed(4)));
    setAllocations((prev) => {
      const next = [...prev];
      next[next.length - 1].ratio = remaining;
      return next;
    });
  };

  // API 호출 핸들러
  const fetchSimulation = async () => {
    if (!isRatioValid) {
      setErrorMsg("비중의 합이 100%여야 합니다.");
      return;
    }

    setLoading(true);
    setErrorMsg("");

    const payload = {
      totalCapital: Number(totalCapital),
      startDate,
      allocations: allocations.map((a) => ({
        symbol: a.symbol,
        ratio: Number(a.ratio.toFixed(4))
      })),
      userProfile: {
        status,
        otherAnnualIncome: Number(otherAnnualIncome) || 0
      }
    };

    try {
      let response;
      try {
        // 1차 시도: 명세서의 공식 직통 엔드포인트
        response = await axios.post("https://n8n.wajok.com/webhook/api/wafire", payload, {
          headers: { "Content-Type": "application/json" },
          timeout: 45000
        });
      } catch (directErr) {
        // 브라우저 환경 CORS preflight 차단 시 내장 프록시 엔드포인트로 자동 폴백
        console.warn("Direct API call failed, falling back to /api/wafire proxy:", directErr.message);
        response = await axios.post("/api/wafire", payload, {
          headers: { "Content-Type": "application/json" },
          timeout: 45000
        });
      }

      if (response && response.data && response.data.summary) {
        setResult(response.data);
      } else {
        throw new Error("서버에서 올바른 시뮬레이션 데이터를 반환하지 않았습니다.");
      }
    } catch (err) {
      console.error("WaFire simulation fetch error:", err);
      setErrorMsg(
        err.response?.data?.message ||
        err.message ||
        "시뮬레이션 연산 중 오류가 발생했습니다. 잠시 후 다시 시도해주세요."
      );
    } finally {
      setLoading(false);
    }
  };

  // 마운트 시 기본 조건으로 자동 시뮬레이션 1회 실행
  useEffect(() => {
    fetchSimulation();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 미선택 종목 목록 (드롭다운 추가용)
  const unselectedSymbols = useMemo(() => {
    const selected = new Set(allocations.map((a) => a.symbol));
    return AVAILABLE_SYMBOLS.filter((s) => !selected.has(s));
  }, [allocations]);

  // =========================================================================
  // 차트 데이터 가공 (Chart.js)
  // =========================================================================

  // 차트 1: 월별 배당금 추이
  const monthlyChartData = useMemo(() => {
    if (!result || !result.monthlyHistory) return null;
    const history = result.monthlyHistory;
    const labels = Object.keys(history).sort();
    const krwData = labels.map((month) => history[month]?.grossKRW || 0);

    return {
      labels,
      datasets: [
        {
          label: "월 배당금 (원화 ₩)",
          data: krwData,
          backgroundColor: "rgba(99, 102, 241, 0.75)",
          hoverBackgroundColor: "rgba(79, 70, 229, 0.95)",
          borderRadius: 6,
          borderSkipped: false
        }
      ]
    };
  }, [result]);

  const monthlyChartOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        tooltip: {
          callbacks: {
            label: (ctx) => {
              const val = ctx.raw || 0;
              const month = ctx.label;
              const usd = result?.monthlyHistory?.[month]?.grossUSD || 0;
              return [
                ` 입금액: ${formatKRW(val)} 원`,
                ` 달러: $${formatUSD(usd)}`
              ];
            }
          }
        }
      },
      scales: {
        x: {
          grid: { display: false },
          ticks: { font: { size: 11 }, maxRotation: 45 }
        },
        y: {
          grid: { color: "rgba(226, 232, 240, 0.6)" },
          ticks: {
            callback: (val) => `${Math.round(val / 10000)}만`,
            font: { size: 11 }
          }
        }
      }
    }),
    [result]
  );

  // 차트 2: 연도별 세금/건보료 뜯긴 내역 (Stacked Bar Chart)
  const yearlyChartData = useMemo(() => {
    if (!result || !result.yearlySummary) return null;
    const summary = result.yearlySummary;
    const years = Object.keys(summary).sort();

    return {
      labels: years.map((y) => `${y}년`),
      datasets: [
        {
          label: "내 손에 남은 실수령액",
          data: years.map((y) => summary[y]?.netKRW || 0),
          backgroundColor: "#10b981",
          borderRadius: 4
        },
        {
          label: "미국 원천징수세 (15%)",
          data: years.map((y) => summary[y]?.usTaxKRW || 0),
          backgroundColor: "#3b82f6",
          borderRadius: 4
        },
        {
          label: "종합소득세 (2천 초과 누진분)",
          data: years.map((y) => summary[y]?.extraTaxKRW || 0),
          backgroundColor: "#f59e0b",
          borderRadius: 4
        },
        {
          label: "건강보험료 타격",
          data: years.map((y) => summary[y]?.healthInsKRW || 0),
          backgroundColor: "#ef4444",
          borderRadius: 4
        }
      ]
    };
  }, [result]);

  const yearlyChartOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: "top",
          labels: { font: { size: 12, weight: "bold" }, boxWidth: 14 }
        },
        tooltip: {
          callbacks: {
            label: (ctx) => ` ${ctx.dataset.label}: ${formatKRW(ctx.raw)} 원`
          }
        }
      },
      scales: {
        x: {
          stacked: true,
          grid: { display: false }
        },
        y: {
          stacked: true,
          grid: { color: "rgba(226, 232, 240, 0.6)" },
          ticks: {
            callback: (val) => `${Math.round(val / 10000)}만`,
            font: { size: 11 }
          }
        }
      }
    }),
    []
  );

  // 연도별 상세 테이블 합계 계산
  const yearlyTotals = useMemo(() => {
    if (!result || !result.yearlySummary) return null;
    const summary = result.yearlySummary;
    const years = Object.keys(summary);
    return years.reduce(
      (acc, y) => {
        acc.grossKRW += summary[y]?.grossKRW || 0;
        acc.usTaxKRW += summary[y]?.usTaxKRW || 0;
        acc.extraTaxKRW += summary[y]?.extraTaxKRW || 0;
        acc.healthInsKRW += summary[y]?.healthInsKRW || 0;
        acc.netKRW += summary[y]?.netKRW || 0;
        return acc;
      },
      { grossKRW: 0, usTaxKRW: 0, extraTaxKRW: 0, healthInsKRW: 0, netKRW: 0 }
    );
  }, [result]);

  return (
    <div className="wafire-page">
      <Container className="pt-4">
        {/* =================================================================== */}
        {/* 1. 상단 히어로 배너 */}
        {/* =================================================================== */}
        <div className="wafire-hero-banner">
          <div className="wafire-hero-badge">
            <FaFire /> AMERICAN DIVIDEND ETF FIRE SIMULATOR
          </div>
          <h1 className="wafire-hero-title">
            와이어 <span style={{ color: "#f43f5e" }}>(와!파이어)</span>
          </h1>
          <p className="wafire-hero-desc">
            미국 월배당 ETF 포트폴리오를 기반으로 조기은퇴(FIRE) 가능성을 검증합니다.
            환율, 주가 변동성, 미국 15% 세금은 물론 <strong>2,000만 원 초과 금융소득종합과세와 건보료 폭탄</strong>까지
            실전 백테스트로 정밀 계산해드립니다 🔥
          </p>
        </div>

        {/* =================================================================== */}
        {/* 2. 상단 컨트롤 패널 (입력 폼) */}
        {/* =================================================================== */}
        <Row>
          {/* 좌측: 자본금 / 시작일 / 신분 선택 */}
          <Col lg={5}>
            <div className="wafire-card">
              <div className="wafire-card-title">
                <span className="d-flex align-items-center gap-2">
                  <span className="wafire-card-title-icon"><FaCoins /></span>
                  투자 자본금 & 조건 설정
                </span>
              </div>

              {/* 총 자본금 */}
              <Form.Group className="mb-3">
                <Form.Label className="small fw-bold text-muted d-flex justify-content-between">
                  <span>총 투자 자본금 (원화)</span>
                  <span className="text-primary fw-bold">{formatKoreanAmount(totalCapital)}</span>
                </Form.Label>
                <div className="wafire-capital-display">
                  {formatKRW(totalCapital)} <span style={{ fontSize: "1rem", color: "#64748b" }}>원</span>
                </div>
                <Form.Control
                  type="number"
                  step="10000000"
                  min="10000000"
                  value={totalCapital}
                  onChange={(e) => setTotalCapital(Number(e.target.value) || 0)}
                  className="rounded-3"
                />
                <div className="wafire-quick-btn-group">
                  <button type="button" className="wafire-quick-btn" onClick={() => handleAddCapital(10000000)}>
                    +1,000만
                  </button>
                  <button type="button" className="wafire-quick-btn" onClick={() => handleAddCapital(50000000)}>
                    +5,000만
                  </button>
                  <button type="button" className="wafire-quick-btn" onClick={() => handleAddCapital(100000000)}>
                    +1억
                  </button>
                  <button type="button" className="wafire-quick-btn" onClick={() => setTotalCapital(300000000)}>
                    기본(3억)
                  </button>
                  <button type="button" className="wafire-quick-btn text-danger" onClick={() => setTotalCapital(100000000)}>
                    1억 리셋
                  </button>
                </div>
              </Form.Group>

              {/* 시작 일자 */}
              <Form.Group className="mb-3">
                <Form.Label className="small fw-bold text-muted d-flex align-items-center gap-1">
                  <FaCalendarAlt /> 백테스트 시작 일자
                </Form.Label>
                <Form.Control
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="rounded-3"
                />
                <div className="d-flex gap-2 mt-2">
                  <button
                    type="button"
                    className="wafire-quick-btn"
                    onClick={() => setStartDate("2023-01-01")}
                  >
                    2023-01-01 (기본)
                  </button>
                  <button
                    type="button"
                    className="wafire-quick-btn"
                    onClick={() => setStartDate("2024-01-01")}
                  >
                    2024-01-01 (최근)
                  </button>
                </div>
              </Form.Group>

              {/* 투자자 신분 토글 */}
              <Form.Group className="mb-3">
                <Form.Label className="small fw-bold text-muted">
                  투자자 신분 (건보료/종합과세 계산 기준)
                </Form.Label>
                <div className="wafire-status-toggle">
                  <button
                    type="button"
                    className={`wafire-status-btn ${status === "UNEMPLOYED" ? "active" : ""}`}
                    onClick={() => setStatus("UNEMPLOYED")}
                  >
                    <span className="d-flex align-items-center gap-1">
                      <FaUmbrellaBeach /> 백수 / 은퇴자
                    </span>
                    <span className="badge-sub">피부양자 / 지역가입자</span>
                  </button>
                  <button
                    type="button"
                    className={`wafire-status-btn ${status === "EMPLOYED" ? "active" : ""}`}
                    onClick={() => setStatus("EMPLOYED")}
                  >
                    <span className="d-flex align-items-center gap-1">
                      <FaUserTie /> 직장인
                    </span>
                    <span className="badge-sub">직장가입자 (소득월액)</span>
                  </button>
                </div>

                {/* 신분별 안내 배너 */}
                <AnimatePresence mode="wait">
                  {status === "UNEMPLOYED" ? (
                    <motion.div
                      key="unemployed-alert"
                      initial={{ opacity: 0, y: -6 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -6 }}
                      className="wafire-tax-alert unemployed"
                    >
                      <FaExclamationTriangle className="me-1" />
                      <strong>피부양자 박탈 경고:</strong> 연 배당소득이 <strong>2,000만 원</strong>을 초과하면
                      피부양자 자격이 즉시 박탈되어 지역가입자로 전환됩니다. 재산과 소득에 따른 건보료 전액이 부과됩니다!
                    </motion.div>
                  ) : (
                    <motion.div
                      key="employed-alert"
                      initial={{ opacity: 0, y: -6 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 0 }}
                      className="wafire-tax-alert employed"
                    >
                      <FaShieldAlt className="me-1" />
                      <strong>소득월액보험료 안내:</strong> 월급 외 금융소득이 <strong>2,000만 원</strong>을 초과하면,
                      초과분에 대해 약 7~8%의 건보료가 회사 월급과 별개로 추가 부과됩니다.
                    </motion.div>
                  )}
                </AnimatePresence>
              </Form.Group>

              {/* 타 소득 (직장인 연봉 또는 사업소득) */}
              <Form.Group className="mb-2">
                <Form.Label className="small fw-bold text-muted d-flex justify-content-between">
                  <span>타 연간 소득 (근로소득/사업 등)</span>
                  <span className="text-secondary">{formatKoreanAmount(otherAnnualIncome)}</span>
                </Form.Label>
                <Form.Control
                  type="number"
                  step="5000000"
                  min="0"
                  placeholder="0"
                  value={otherAnnualIncome}
                  onChange={(e) => setOtherAnnualIncome(Number(e.target.value) || 0)}
                  className="rounded-3"
                />
                <Form.Text className="text-muted small">
                  2,000만 원 초과 종합과세 시 누진세율(6%~45%) 구간 계산에 적용됩니다.
                </Form.Text>
              </Form.Group>
            </div>
          </Col>

          {/* 우측: ETF 포트폴리오 비중 배분 인터페이스 */}
          <Col lg={7}>
            <div className="wafire-card">
              <div className="wafire-card-title">
                <span className="d-flex align-items-center gap-2">
                  <span className="wafire-card-title-icon"><FaBalanceScale /></span>
                  ETF 포트폴리오 비중 배분
                </span>
                <div className="d-flex gap-2">
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary rounded-pill px-3 fw-bold"
                    onClick={handleEqualWeight}
                  >
                    1/N 균등 배분
                  </button>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-primary rounded-pill px-3 fw-bold"
                    onClick={handleAutoAdjust}
                  >
                    100% 자동 맞춤
                  </button>
                </div>
              </div>

              {/* 선택된 ETF 슬라이더 목록 */}
              <div className="wafire-allocations-list">
                {allocations.map((item, idx) => {
                  const meta = ETF_DATABASE[item.symbol] || {};
                  const percentVal = Math.round((item.ratio || 0) * 100);
                  const allocatedKRW = totalCapital * (item.ratio || 0);

                  return (
                    <div key={item.symbol} className="wafire-allocation-card">
                      <div className="d-flex align-items-center justify-content-between mb-2">
                        <div className="wafire-symbol-badge">
                          <span>{item.symbol}</span>
                          <span className="wafire-symbol-tag">{meta.tag || "월배당 ETF"}</span>
                          <span className="wafire-symbol-yield">예상 {meta.yieldEst}</span>
                        </div>
                        <div className="d-flex align-items-center gap-2">
                          <span className="small text-muted">{formatKoreanAmount(allocatedKRW)}</span>
                          <div className="wafire-slider-val">{percentVal}%</div>
                          {allocations.length > 1 && (
                            <button
                              type="button"
                              className="btn btn-sm text-danger p-0 ms-2"
                              onClick={() => handleRemoveSymbol(idx)}
                              title={`${item.symbol} 삭제`}
                            >
                              <FaTrashAlt />
                            </button>
                          )}
                        </div>
                      </div>

                      <div className="small text-muted mb-2 text-truncate" title={meta.desc}>
                        {meta.desc}
                      </div>

                      {/* 슬라이더 & 숫자 인풋 */}
                      <div className="d-flex align-items-center gap-3">
                        <Form.Range
                          min="0"
                          max="100"
                          step="1"
                          value={percentVal}
                          onChange={(e) => handleRatioChange(idx, e.target.value)}
                          className="flex-grow-1"
                        />
                        <Form.Control
                          type="number"
                          min="0"
                          max="100"
                          style={{ width: "70px" }}
                          value={percentVal}
                          onChange={(e) => handleRatioChange(idx, e.target.value)}
                          className="text-center rounded-3 fw-bold py-1"
                        />
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* ETF 종목 추가 드롭다운 */}
              {unselectedSymbols.length > 0 && (
                <div className="mt-3 d-flex align-items-center gap-2">
                  <Form.Select
                    defaultValue=""
                    onChange={(e) => {
                      if (e.target.value) {
                        handleAddSymbol(e.target.value);
                        e.target.value = "";
                      }
                    }}
                    className="rounded-3"
                  >
                    <option value="" disabled>
                      + 추가할 미국 월배당 ETF 선택...
                    </option>
                    {unselectedSymbols.map((sym) => (
                      <option key={sym} value={sym}>
                        {sym} ({ETF_DATABASE[sym]?.name} • {ETF_DATABASE[sym]?.tag})
                      </option>
                    ))}
                  </Form.Select>
                </div>
              )}

              {/* 비중 합계 검증 배지 */}
              <div className={`wafire-ratio-sum-box ${isRatioValid ? "valid" : "invalid"}`}>
                <div className="d-flex align-items-center gap-2">
                  {isRatioValid ? <FaCheckCircle /> : <FaExclamationTriangle />}
                  <span>
                    {isRatioValid
                      ? "비중 합계가 정확히 100%입니다."
                      : `비중의 합이 100%여야 합니다 (현재 ${totalPercent}%)`}
                  </span>
                </div>
                <div className="fs-5">{totalPercent}%</div>
              </div>

              {/* 에러 메시지 알림 */}
              {errorMsg && (
                <Alert variant="danger" className="mt-3 py-2 small rounded-3">
                  {errorMsg}
                </Alert>
              )}

              {/* 실행 버튼 */}
              <button
                type="button"
                className="wafire-submit-btn"
                onClick={fetchSimulation}
                disabled={loading || !isRatioValid}
              >
                {loading ? (
                  <>
                    <Spinner size="sm" animation="border" className="me-2" />
                    n8n 금융 엔진에서 실전 백테스트 연산 중...
                  </>
                ) : (
                  <>
                    <FaFire /> 회사 탈출 각 재보기 (와!파이어 계산하기)
                  </>
                )}
              </button>
            </div>
          </Col>
        </Row>

        {/* =================================================================== */}
        {/* 3. 시뮬레이션 결과 섹션 */}
        {/* =================================================================== */}
        {result && result.summary && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="mt-4"
          >
            {/* A. 핵심 요약 KPI 카드 그리드 */}
            <div className="wafire-kpi-grid">
              {/* 1. 월평균 실수령액 (주인공 카드) */}
              <div className="wafire-kpi-card highlight">
                <div className="wafire-kpi-label">🔥 월평균 실수령 배당금</div>
                <div className="wafire-kpi-val">
                  매월 {formatKRW(result.summary.avgMonthlyNetKRW)} 원 겟!
                </div>
                <div className="wafire-kpi-sub">
                  세금과 건보료를 모두 공제한 순수 통장 꽂힘액
                </div>
                <div className="wafire-frog-verdict">
                  {getFrogVerdict(result.summary.avgMonthlyNetKRW).title}
                </div>
              </div>

              {/* 2. 원금 평가 및 방어율 */}
              <div className="wafire-kpi-card">
                <div className="wafire-kpi-label">📈 원금 방어율 & 주가 수익률</div>
                <div
                  className="wafire-kpi-val"
                  style={{
                    color: (result.summary.capitalReturnRate || 0) >= 0 ? "#10b981" : "#ef4444"
                  }}
                >
                  {(result.summary.capitalReturnRate || 0) >= 0 ? "+" : ""}
                  {result.summary.capitalReturnRate}%
                </div>
                <div className="wafire-kpi-sub">
                  원금 {formatKoreanAmount(result.summary.totalInvestedKRW)} ➔{" "}
                  <strong>{formatKoreanAmount(result.summary.currentEvaluationKRW)}</strong>
                </div>
              </div>

              {/* 3. 누적 세전 vs 실수령 */}
              <div className="wafire-kpi-card">
                <div className="wafire-kpi-label">💰 누적 총 배당금 비교</div>
                <div className="wafire-kpi-val" style={{ color: "#4f46e5" }}>
                  {formatKoreanAmount(result.summary.totalNetDivKRW)}
                </div>
                <div className="wafire-kpi-sub">
                  세전 {formatKoreanAmount(result.summary.totalGrossDivKRW)} 중{" "}
                  <span className="text-danger fw-bold">
                    {formatKoreanAmount(
                      result.summary.totalGrossDivKRW - result.summary.totalNetDivKRW
                    )}
                  </span>{" "}
                  공제됨
                </div>
              </div>

              {/* 4. 세금 및 건보료 실효 공제율 */}
              <div className="wafire-kpi-card">
                <div className="wafire-kpi-label">🛡️ 세금/건보료 실효 공제율</div>
                <div className="wafire-kpi-val" style={{ color: "#d97706" }}>
                  {result.summary.totalGrossDivKRW > 0
                    ? (
                        ((result.summary.totalGrossDivKRW - result.summary.totalNetDivKRW) /
                          result.summary.totalGrossDivKRW) *
                        100
                      ).toFixed(1)
                    : 0}
                  %
                </div>
                <div className="wafire-kpi-sub">
                  미국세 15% + 금융소득종합과세 + 건보료 합산
                </div>
              </div>
            </div>

            {/* B. 시각화 차트 영역 */}
            <Row className="mb-4">
              {/* 차트 1: 월별 배당금 추이 */}
              <Col lg={7} className="mb-4 mb-lg-0">
                <div className="wafire-card h-100">
                  <div className="wafire-card-title">
                    <span className="d-flex align-items-center gap-2">
                      <span className="wafire-card-title-icon"><FaChartLine /></span>
                      월별 배당금 입금 추이 (원화 ₩)
                    </span>
                    <span className="badge bg-light text-dark fw-bold">
                      {monthlyChartData?.labels?.length || 0}개월 누적
                    </span>
                  </div>
                  <div className="wafire-chart-wrapper">
                    {monthlyChartData && <Bar data={monthlyChartData} options={monthlyChartOptions} />}
                  </div>
                </div>
              </Col>

              {/* 차트 2: 연도별 세금/건보료 뜯긴 내역 */}
              <Col lg={5}>
                <div className="wafire-card h-100">
                  <div className="wafire-card-title">
                    <span className="d-flex align-items-center gap-2">
                      <span className="wafire-card-title-icon"><FaFileInvoiceDollar /></span>
                      연도별 실수령 vs 세금/건보료
                    </span>
                    <span className="badge bg-danger-subtle text-danger fw-bold">공제 내역</span>
                  </div>
                  <div className="wafire-chart-wrapper">
                    {yearlyChartData && <Bar data={yearlyChartData} options={yearlyChartOptions} />}
                  </div>
                </div>
              </Col>
            </Row>

            {/* C. 연도별 상세 내역 테이블 */}
            <div className="wafire-card mb-4">
              <div className="wafire-card-title">
                <span className="d-flex align-items-center gap-2">
                  <span className="wafire-card-title-icon"><FaMoneyBillWave /></span>
                  연도별 배당금 및 공제 상세 내역
                </span>
                <span className="small text-muted">단위: 원화(₩)</span>
              </div>

              <div className="wafire-table-responsive">
                <table className="wafire-table">
                  <thead>
                    <tr>
                      <th>연도</th>
                      <th>세전 총 배당금</th>
                      <th>미국 원천징수 (15%)</th>
                      <th>국내 종합소득세</th>
                      <th>건강보험료 타격</th>
                      <th>최종 실수령액</th>
                      <th>실수령 비율</th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.yearlySummary &&
                      Object.keys(result.yearlySummary)
                        .sort()
                        .map((year) => {
                          const item = result.yearlySummary[year];
                          const netRatio = item.grossKRW > 0 ? ((item.netKRW / item.grossKRW) * 100).toFixed(1) : "0.0";
                          return (
                            <tr key={year}>
                              <td>{year}년</td>
                              <td>{formatKRW(item.grossKRW)} 원</td>
                              <td className="text-secondary">-{formatKRW(item.usTaxKRW)} 원</td>
                              <td className="text-warning">
                                {item.extraTaxKRW > 0 ? `-${formatKRW(item.extraTaxKRW)} 원` : "0 원"}
                              </td>
                              <td className="text-danger">
                                {item.healthInsKRW > 0 ? `-${formatKRW(item.healthInsKRW)} 원` : "0 원"}
                              </td>
                              <td className="fw-bold text-success">
                                {formatKRW(item.netKRW)} 원
                              </td>
                              <td>
                                <span className="badge bg-success-subtle text-success fw-bold">
                                  {netRatio}%
                                </span>
                              </td>
                            </tr>
                          );
                        })}

                    {/* 합계 행 */}
                    {yearlyTotals && (
                      <tr className="total-row">
                        <td>총 합계</td>
                        <td>{formatKRW(yearlyTotals.grossKRW)} 원</td>
                        <td className="text-secondary">-{formatKRW(yearlyTotals.usTaxKRW)} 원</td>
                        <td className="text-warning">-{formatKRW(yearlyTotals.extraTaxKRW)} 원</td>
                        <td className="text-danger">-{formatKRW(yearlyTotals.healthInsKRW)} 원</td>
                        <td className="fw-bold text-success fs-6">
                          {formatKRW(yearlyTotals.netKRW)} 원
                        </td>
                        <td>
                          <span className="badge bg-primary text-white fw-bold">
                            {yearlyTotals.grossKRW > 0
                              ? ((yearlyTotals.netKRW / yearlyTotals.grossKRW) * 100).toFixed(1)
                              : "0.0"}
                            %
                          </span>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* D. ETF 보유 종목별 매수단가 & 수익률 현황 */}
            {result.holdings && (
              <div className="wafire-card">
                <div className="wafire-card-title">
                  <span className="d-flex align-items-center gap-2">
                    <span className="wafire-card-title-icon"><FaExchangeAlt /></span>
                    ETF 포트폴리오 보유 종목별 상세 현황
                  </span>
                  <span className="small text-muted">최근 환율: ₩{result.holdings[Object.keys(result.holdings)[0]]?.latestFx || 1350}</span>
                </div>

                <div className="wafire-holdings-grid">
                  {Object.keys(result.holdings).map((sym) => {
                    const h = result.holdings[sym];
                    const returnRate =
                      h.buyPriceUSD > 0
                        ? (((h.currentPriceUSD - h.buyPriceUSD) / h.buyPriceUSD) * 100).toFixed(2)
                        : "0.00";
                    const isPositive = Number(returnRate) >= 0;

                    return (
                      <div key={sym} className="wafire-holding-card">
                        <div className="wafire-holding-title">
                          <span className="wafire-holding-symbol">{sym}</span>
                          <span
                            className={`badge ${isPositive ? "bg-success-subtle text-success" : "bg-danger-subtle text-danger"}`}
                          >
                            {isPositive ? "+" : ""}
                            {returnRate}%
                          </span>
                        </div>
                        <div className="wafire-holding-row">
                          <span>보유 수량</span>
                          <span>{h.shares?.toLocaleString()} 주</span>
                        </div>
                        <div className="wafire-holding-row">
                          <span>매수 단가</span>
                          <span>${formatUSD(h.buyPriceUSD)}</span>
                        </div>
                        <div className="wafire-holding-row">
                          <span>현재가</span>
                          <span>${formatUSD(h.currentPriceUSD)}</span>
                        </div>
                        <div className="wafire-holding-row">
                          <span>투자 원금</span>
                          <span>{formatKoreanAmount(h.investedKRW)}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </motion.div>
        )}
      </Container>
    </div>
  );
};

export default WaFireSimulator;
